import Seo from '@comp/seo'
import { motion } from 'framer-motion'
import Layout from '@comp/layout'
import TrustBand from '@comp/home/trust-band'
import ContactForm from '@comp/contact/contact-form'
import OfficeGrid, { Office } from '@comp/company/office-grid'
import Container from '@comp/ui/container'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

const offices: Office[] = [
  {
    city: 'New York',
    country: 'United States',
    address: '99 Wall Street #5534',
    isHq: true,
  },
  {
    city: 'Strasbourg',
    country: 'France',
    address: '14B Rue du Maréchal Lefebvre',
  },
  {
    city: 'Dubai',
    country: 'United Arab Emirates',
    address:
      'Area 271, Emirates Towers Boulevard, Trade Centre 2, Sheikh Zayed Road',
  },
  {
    city: 'Istanbul',
    country: 'Türkiye',
    address: 'Dijitalpark Teknokent PK: 34788 Çekmeköy',
  },
]

const contactChannels = [
  {
    label: 'Sales',
    description: 'Pricing, custom plans, and enterprise procurement.',
    email: 'sales@monofor.com',
  },
  {
    label: 'Support',
    description: 'Help with an existing account or trial.',
    email: 'support@monofor.com',
  },
  {
    label: 'Press',
    description: 'Media, analyst, and partnership inquiries.',
    email: 'hello@monofor.com',
  },
]

export default function ContactPage() {
  return (
    <Layout>
      <Seo
        title="Contact Monofor: Talk to our experts"
        description="Talk to our experts in IAM, PAM, and IGA. Reach out for sales, support, partnerships, or to find your nearest office."
        path="/contact"
      />
      <main className="bg-surface text-ink-primary">
        <section className="relative overflow-hidden bg-surface pt-20 pb-24 lg:pt-28 lg:pb-32">
          <BackgroundField />
          <Container size="xl" className="relative">
            <div className="grid items-start gap-16 lg:grid-cols-[1fr_1fr] lg:gap-20">
              {/* Left: messaging + channels */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                >
                  <Eyebrow tone="brand">Contact</Eyebrow>
                </motion.div>
                <motion.div
                  className="mt-6"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: EASE, delay: 0.05 }}
                >
                  <Heading as="h1" size="display-xl">
                    Talk to our
                    <br />
                    identity experts.
                  </Heading>
                </motion.div>
                <motion.div
                  className="mt-6 max-w-md"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: EASE, delay: 0.12 }}
                >
                  <Text size="xl" tone="secondary">
                    Whether you are evaluating, scaling, or simply curious, our
                    team is here. Send a message, or reach the right channel
                    directly.
                  </Text>
                </motion.div>

                <div className="mt-10 space-y-3">
                  {contactChannels.map((c, i) => (
                    <motion.a
                      key={c.email}
                      href={`mailto:${c.email}`}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.5,
                        ease: EASE,
                        delay: 0.4 + i * 0.08,
                      }}
                      className="group flex items-center justify-between rounded-2xl border border-line-subtle bg-surface px-5 py-4 transition duration-200 ease-smooth hover:border-line hover:shadow-card"
                    >
                      <div>
                        <div className="text-body-md font-medium text-ink-primary">
                          {c.label}
                        </div>
                        <div className="mt-0.5 text-body-sm text-ink-tertiary">
                          {c.description}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-body-sm font-medium text-brand-900 transition group-hover:translate-x-0.5">
                        {c.email}
                        <span aria-hidden>→</span>
                      </div>
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* Right: form */}
              <ContactForm />
            </div>
          </Container>
        </section>

        <OfficeGrid offices={offices} />
        <TrustBand />
      </main>
    </Layout>
  )
}

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/70 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/60 blur-3xl" />
      <svg
        className="absolute inset-0 h-full w-full text-brand-900/[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="grid-contact"
            width="56"
            height="56"
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 56V.5H56" fill="none" stroke="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-contact)" />
      </svg>
    </div>
  )
}
